import * as ort from 'onnxruntime-web/wasm'
import type { Move, Piece } from '../stores/gameStore'
import { BOARD_H, BOARD_W, NUM_POSITIONS, POSITIONS, GRID_INDICES } from './neuralData'
import { generateMoves } from './moveGen'
import { getPlayer1Home, getPlayer2Home } from '../utils/board'

// Direct in-browser inference for the trained candidate_v2 (iter400) network.
// We build the exact observation + candidate action space the model was trained
// on (validated 40/40 against the JAX env) and run the ONNX model ourselves —
// bypassing the cc_core WASM MCTS engine, whose move generation didn't match the
// game rules (it produced ~7% illegal actions and mismapped some moves).

const MODEL_URL = '/models/candidate_v2_iter400_fp16.onnx'
const MAX_ACTIONS = 1216

const posToIndex = new Map<string, number>()
POSITIONS.forEach(([q, r], i) => posToIndex.set(`${q},${r}`, i))

// CPU (wasm) inference, multi-threaded. The net is ~0.9 GMAC per position, so it is
// compute-bound: measured in real Firefox 158 / Chromium on Linux, 1 thread ≈ 42 ms per
// position, 8 threads ≈ 6 ms (near-linear). Threads need crossOriginIsolated (COOP/COEP
// in vercel.json + vite.config.ts); without it ORT silently falls back to 1 thread.
// The runtime is served unbundled from /ort/ (see copyOrtRuntime in vite.config.ts) —
// bundling it is what crashed the pthread workers before ("document is not defined").
// WebGPU was dropped: it froze on Linux Firefox and starved the WebGL board, and its
// JSEP/asyncify binary is a 25 MB download vs 12 MB for plain wasm.
ort.env.wasm.wasmPaths = '/ort/'
const cores = typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 4 : 4
const crossOriginIsolatedOk = typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated
ort.env.wasm.numThreads = crossOriginIsolatedOk ? Math.min(8, Math.max(1, cores - 1)) : 1

let sessionPromise: Promise<ort.InferenceSession> | null = null
function getSession(): Promise<ort.InferenceSession> {
  if (!sessionPromise) {
    sessionPromise = ort.InferenceSession.create(MODEL_URL, { executionProviders: ['wasm'] })
    void sessionPromise
      .then(() => console.info(`[neural] backend: wasm (CPU), ${ort.env.wasm.numThreads} thread(s)`))
      .catch(() => {})
  }
  return sessionPromise
}

// Warm up in the background so the first real move isn't slowed by model load.
export function warmupNeuralPolicy(): void {
  void getSession().catch(() => {
    sessionPromise = null
  })
}

// Canonical observation [H, W, 3] = (my, opponent, valid) grids, flipped for
// player 2 — a faithful port of ChineseCheckersJAX.get_observation.
function buildObservation(pieces: Piece[], player: number): Float32Array {
  const obs = new Float32Array(BOARD_H * BOARD_W * 3)
  const flip = player === 2
  const opponent = player === 1 ? 2 : 1

  const occupant = new Array<number>(NUM_POSITIONS).fill(0)
  for (const p of pieces) {
    const idx = posToIndex.get(`${p.position.q},${p.position.r}`)
    if (idx !== undefined) occupant[idx] = p.player
  }

  for (let i = 0; i < NUM_POSITIONS; i++) {
    const gx = Math.floor(GRID_INDICES[i] / BOARD_W)
    const gy = GRID_INDICES[i] % BOARD_W
    const fx = flip ? BOARD_H - 1 - gx : gx
    const fy = flip ? BOARD_W - 1 - gy : gy
    const base = (fx * BOARD_W + fy) * 3
    obs[base + 2] = 1 // valid grid
    if (occupant[i] === player) obs[base] += 1 // my grid
    else if (occupant[i] === opponent) obs[base + 1] += 1 // opponent grid
  }
  return obs
}

export interface NeuralPolicyResult {
  move: Move | null
  value: number
  elapsedMs: number
}

// Run the ONNX model for a batch of B positions. Returns raw policy logits
// ([B*MAX_ACTIONS]) and values ([B]).
async function runModel(
  obs: Float32Array,
  candidateMoves: Int32Array,
  candidateMask: Uint8Array,
  players: Int32Array,
  batch: number,
): Promise<{ logits: Float32Array; values: Float32Array }> {
  const session = await getSession()
  const out = await session.run({
    board: new ort.Tensor('float32', obs, [batch, BOARD_H, BOARD_W, 3]),
    candidate_moves: new ort.Tensor('int32', candidateMoves, [batch, MAX_ACTIONS]),
    candidate_mask: new ort.Tensor('bool', candidateMask, [batch, MAX_ACTIONS]),
    current_player: new ort.Tensor('int32', players, [batch]),
  })
  return { logits: out.prior_logits.data as Float32Array, values: out.value.data as Float32Array }
}

// Encode the current player's legal moves as candidate slots for one position.
function encodeCandidates(legalMoves: Move[]): { moves: Int32Array; mask: Uint8Array; n: number } {
  const n = Math.min(legalMoves.length, MAX_ACTIONS)
  const moves = new Int32Array(MAX_ACTIONS)
  const mask = new Uint8Array(MAX_ACTIONS)
  for (let i = 0; i < n; i++) {
    const m = legalMoves[i]
    const s = posToIndex.get(`${m.start.q},${m.start.r}`)
    const e = posToIndex.get(`${m.end.q},${m.end.r}`)
    if (s === undefined || e === undefined) continue
    moves[i] = s * NUM_POSITIONS + e
    mask[i] = 1
  }
  return { moves, mask, n }
}

// Apply a move on the piece list (piece jumps/steps from start to end).
function applyNeuralMove(pieces: Piece[], move: Move, player: number): Piece[] {
  return pieces.map((p) =>
    p.player === player && p.position.q === move.start.q && p.position.r === move.start.r
      ? { position: { q: move.end.q, r: move.end.r }, player: p.player }
      : p,
  )
}

// hard: pick the highest-policy legal move (one forward pass).
export async function selectNeuralPolicyMove(config: {
  pieces: Piece[]
  currentPlayer: number
  legalMoves: Move[]
}): Promise<NeuralPolicyResult> {
  const { pieces, currentPlayer, legalMoves } = config
  const t0 = performance.now()
  if (legalMoves.length === 0) return { move: null, value: 0, elapsedMs: 0 }

  const { moves, mask, n } = encodeCandidates(legalMoves)
  const { logits, values } = await runModel(
    buildObservation(pieces, currentPlayer),
    moves,
    mask,
    new Int32Array([currentPlayer]),
    1,
  )

  let bestIdx = 0
  let bestLogit = -Infinity
  for (let i = 0; i < n; i++) {
    if (mask[i] && logits[i] > bestLogit) {
      bestLogit = logits[i]
      bestIdx = i
    }
  }
  return { move: legalMoves[bestIdx], value: values[0], elapsedMs: Math.round(performance.now() - t0) }
}

// ── advanced: policy + value guided MCTS (PUCT), the way the model was trained ──

const C_PUCT = 1.5
const MCTS_SIMS = 64

// A player wins once all its marbles occupy the opponent's home triangle.
const TARGET_HOME: Record<number, Set<string>> = {
  1: getPlayer2Home(),
  2: getPlayer1Home(),
}
// A player's own home — needed so the search's look-ahead move generation honours
// the same "a marble in the target home can't leave" rule the engine enforces.
// Without this the MCTS imagined marbles leaving/re-entering home and mis-played
// the endgame (shuffling instead of finishing).
const HOME_ZONE: Record<number, Set<string>> = {
  1: getPlayer1Home(),
  2: getPlayer2Home(),
}
function isWin(pieces: Piece[], player: number): boolean {
  const home = TARGET_HOME[player]
  for (const p of pieces) {
    if (p.player === player && !home.has(`${p.position.q},${p.position.r}`)) return false
  }
  return true
}

interface MctsNode {
  pieces: Piece[]
  player: number
  legal: Move[]
  priors: Float32Array
  children: (MctsNode | null)[]
  N: Int32Array
  W: Float32Array
  totalN: number
  terminal: boolean
  termValue: number // value from this node's player's perspective
}

// Softmax a slice of the model's policy logits (starting at `offset`) into priors
// and wrap them in a fresh, unexpanded MCTS node. Shared by the root expansion and
// the batched-leaf expansion so both use identical math.
function buildNode(
  pieces: Piece[],
  player: number,
  legal: Move[],
  logits: Float32Array,
  offset: number,
): MctsNode {
  const nn = legal.length
  let max = -Infinity
  for (let i = 0; i < nn; i++) if (logits[offset + i] > max) max = logits[offset + i]
  const priors = new Float32Array(nn)
  let sum = 0
  for (let i = 0; i < nn; i++) {
    const e = Math.exp(logits[offset + i] - max)
    priors[i] = e
    sum += e
  }
  for (let i = 0; i < nn; i++) priors[i] /= sum || 1
  return {
    pieces, player, legal, priors,
    children: new Array(nn).fill(null),
    N: new Int32Array(nn), W: new Float32Array(nn), totalN: 0,
    terminal: false, termValue: 0,
  }
}

function terminalNode(pieces: Piece[], player: number, termValue: number): MctsNode {
  return {
    pieces, player, legal: [], priors: new Float32Array(0), children: [],
    N: new Int32Array(0), W: new Float32Array(0), totalN: 0, terminal: true, termValue,
  }
}

// Expand a node: run the model (batch of 1) to get child priors + net value (from
// `player`'s perspective). `legalOverride` lets the root use the engine's
// authoritative legal moves; look-ahead legality honours the goal-zone rule.
async function expandNode(pieces: Piece[], player: number, legalOverride?: Move[]): Promise<{ node: MctsNode; value: number }> {
  const legal =
    legalOverride ??
    generateMoves(pieces, player, { goalZone: TARGET_HOME[player], homeZone: HOME_ZONE[player] })
  if (legal.length === 0) return { node: terminalNode(pieces, player, 0), value: 0 }

  const { moves, mask } = encodeCandidates(legal)
  const { logits, values } = await runModel(buildObservation(pieces, player), moves, mask, new Int32Array([player]), 1)
  return { node: buildNode(pieces, player, legal, logits, 0), value: values[0] }
}

function selectChild(node: MctsNode): number {
  let best = -Infinity
  let bestIdx = 0
  const sqrtTotal = Math.sqrt(node.totalN + 1)
  for (let i = 0; i < node.legal.length; i++) {
    const q = node.N[i] > 0 ? node.W[i] / node.N[i] : 0
    const u = (C_PUCT * node.priors[i] * sqrtTotal) / (1 + node.N[i])
    if (q + u > best) {
      best = q + u
      bestIdx = i
    }
  }
  return bestIdx
}

const BATCH_SIZE = 16 // leaves gathered per batched forward pass (fewer, larger GPU dispatches)

interface PathStep {
  node: MctsNode
  i: number
}

// Back a leaf value (from the leaf player's perspective) up the collected path,
// flipping sign each level (negamax). The descent already bumped N/totalN as the
// node's one real visit, so here we only add the value. (Verified against a mock:
// at BATCH_SIZE=1 this reproduces the old sequential MCTS's visit counts exactly.)
function backup(path: PathStep[], leafValue: number): void {
  let v = leafValue
  for (let d = path.length - 1; d >= 0; d--) {
    v = -v
    const { node, i } = path[d]
    node.W[i] += v
  }
}

// Undo the visit bumps for a descent we abandoned (leaf already claimed this round).
function revertVirtualLoss(path: PathStep[]): void {
  for (const { node, i } of path) {
    node.N[i]--
    node.totalN--
  }
}

// advanced: batched-leaf PUCT MCTS. Each round gathers up to BATCH_SIZE distinct
// leaves via virtual-loss descent, evaluates them all in ONE forward pass, then
// expands + backs up each — turning MCTS_SIMS tiny sequential inferences into a
// handful of batched ones (the key to making WebGPU pay off). Plays the most-
// visited root move and finds finishing moves via the terminal-win shortcut.
export async function selectNeuralMctsMove(config: {
  pieces: Piece[]
  currentPlayer: number
  legalMoves: Move[]
}): Promise<NeuralPolicyResult> {
  const { pieces, currentPlayer, legalMoves } = config
  const t0 = performance.now()
  if (legalMoves.length === 0) return { move: null, value: 0, elapsedMs: 0 }

  const { node: root } = await expandNode(pieces, currentPlayer, legalMoves)
  if (root.legal.length === 0) return { move: null, value: 0, elapsedMs: Math.round(performance.now() - t0) }

  const stride = BOARD_H * BOARD_W * 3
  let done = 0
  while (done < MCTS_SIMS) {
    // ── collect a batch of distinct leaves via virtual-loss descent ──
    const batch: { path: PathStep[]; pieces: Piece[]; player: number }[] = []
    const claimed = new Map<MctsNode, Set<number>>() // (parent → child slots) claimed this round

    while (batch.length < BATCH_SIZE && done + batch.length < MCTS_SIMS) {
      const path: PathStep[] = []
      let node = root
      let collided = false
      for (;;) {
        if (node.terminal) {
          backup(path, node.termValue)
          done++
          break
        }
        const i = selectChild(node)
        // Virtual loss: bump visit counts only (never W) so another descent in this
        // same batch is steered to a different leaf, while W stays identical to
        // sequential MCTS. selectChild's 1+N and √totalN terms do the discouraging.
        node.N[i]++
        node.totalN++
        path.push({ node, i })
        if (node.children[i] === null) {
          let slots = claimed.get(node)
          if (slots?.has(i)) {
            revertVirtualLoss(path) // this leaf is already in the batch — abandon this descent
            collided = true
            break
          }
          if (!slots) {
            slots = new Set()
            claimed.set(node, slots)
          }
          slots.add(i)
          const childPieces = applyNeuralMove(node.pieces, node.legal[i], node.player)
          const nextPlayer = node.player === 1 ? 2 : 1
          if (isWin(childPieces, node.player)) {
            node.children[i] = terminalNode(childPieces, nextPlayer, -1)
            backup(path, -1) // leaf (loser-to-move) value = -1 ⇒ +1 for the winner
            done++
            break
          }
          batch.push({ path, pieces: childPieces, player: nextPlayer })
          break
        }
        node = node.children[i] as MctsNode
      }
      if (collided) break // process what we have; virtual loss keeps the next round fresh
    }

    // ── one batched forward pass, then expand + back up every gathered leaf ──
    if (batch.length > 0) {
      const B = batch.length
      const obs = new Float32Array(B * stride)
      const moves = new Int32Array(B * MAX_ACTIONS)
      const mask = new Uint8Array(B * MAX_ACTIONS)
      const players = new Int32Array(B)
      const legals: Move[][] = []
      for (let b = 0; b < B; b++) {
        const it = batch[b]
        const legal = generateMoves(it.pieces, it.player, {
          goalZone: TARGET_HOME[it.player],
          homeZone: HOME_ZONE[it.player],
        })
        legals.push(legal)
        obs.set(buildObservation(it.pieces, it.player), b * stride)
        const enc = encodeCandidates(legal)
        moves.set(enc.moves, b * MAX_ACTIONS)
        mask.set(enc.mask, b * MAX_ACTIONS)
        players[b] = it.player
      }
      const { logits, values } = await runModel(obs, moves, mask, players, B)
      for (let b = 0; b < B; b++) {
        const it = batch[b]
        const last = it.path[it.path.length - 1]
        const legal = legals[b]
        if (legal.length === 0) {
          last.node.children[last.i] = terminalNode(it.pieces, it.player, 0)
          backup(it.path, 0)
        } else {
          last.node.children[last.i] = buildNode(it.pieces, it.player, legal, logits, b * MAX_ACTIONS)
          backup(it.path, values[b])
        }
        done++
      }
    }

    // Yield to the event loop so the 3D scene keeps rendering during the search.
    await new Promise((resolve) => setTimeout(resolve))
  }

  let bestIdx = 0
  let bestN = -1
  for (let i = 0; i < root.legal.length; i++) {
    if (root.N[i] > bestN) {
      bestN = root.N[i]
      bestIdx = i
    }
  }
  const q = root.N[bestIdx] > 0 ? root.W[bestIdx] / root.N[bestIdx] : 0
  return { move: root.legal[bestIdx], value: q, elapsedMs: Math.round(performance.now() - t0) }
}

// ── dev diagnostics: run `window.__ccbench()` in the console to compare the active
// backend. Measures warm inference latency by batch size + a full Advanced move. ──
export async function benchmarkNeural(): Promise<void> {
  const { createNewGame } = await import('../game/engine')
  const game = createNewGame()
  const player = game.currentPlayer
  const enc = encodeCandidates(game.legalMoves)
  const single = buildObservation(game.pieces, player)
  const stride = BOARD_H * BOARD_W * 3

  const timeInfer = async (B: number, runs: number): Promise<number> => {
    const obs = new Float32Array(B * stride)
    const moves = new Int32Array(B * MAX_ACTIONS)
    const mask = new Uint8Array(B * MAX_ACTIONS)
    const players = new Int32Array(B)
    for (let b = 0; b < B; b++) {
      obs.set(single, b * stride)
      moves.set(enc.moves, b * MAX_ACTIONS)
      mask.set(enc.mask, b * MAX_ACTIONS)
      players[b] = player
    }
    await runModel(obs, moves, mask, players, B) // warm (compiles shaders on WebGPU)
    const start = performance.now()
    for (let r = 0; r < runs; r++) await runModel(obs, moves, mask, players, B)
    return (performance.now() - start) / runs
  }

  const table: Record<string, { msPerCall: number; msPerState: number }> = {}
  for (const B of [1, 8, 16, 32, 64]) {
    const ms = await timeInfer(B, 10)
    table[`batch ${B}`] = { msPerCall: +ms.toFixed(1), msPerState: +(ms / B).toFixed(2) }
  }
  console.table(table)

  const start = performance.now()
  const res = await selectNeuralMctsMove({ pieces: game.pieces, currentPlayer: player, legalMoves: game.legalMoves })
  console.info(`[ccbench] full Advanced move: ${Math.round(performance.now() - start)}ms for ${MCTS_SIMS} sims — move`, res.move)
}

// Smoke test: play a few real Advanced moves and confirm each is legal. Returns a
// summary object. Run `await window.__ccSmoke(6)` in the console (or headless).
export async function smokeSelfPlay(moves = 6): Promise<unknown> {
  const { createNewGame, applyMove } = await import('../game/engine')
  let state = createNewGame()
  const log: { ms: number; legalBefore: number; ok: boolean }[] = []
  for (let k = 0; k < moves && state.winner === null; k++) {
    const r = await selectNeuralMctsMove({
      pieces: state.pieces,
      currentPlayer: state.currentPlayer,
      legalMoves: state.legalMoves,
    })
    const ok =
      !!r.move &&
      state.legalMoves.some(
        (m) =>
          m.start.q === r.move!.start.q &&
          m.start.r === r.move!.start.r &&
          m.end.q === r.move!.end.q &&
          m.end.r === r.move!.end.r,
      )
    log.push({ ms: r.elapsedMs, legalBefore: state.legalMoves.length, ok })
    if (!r.move) break
    state = applyMove(state, r.move)
  }
  return { moves: log, winner: state.winner, allLegal: log.every((x) => x.ok) }
}

if (typeof window !== 'undefined') {
  const w = window as unknown as {
    __ccbench?: () => Promise<void>
    __ccSmoke?: (moves?: number) => Promise<unknown>
  }
  w.__ccbench = benchmarkNeural
  w.__ccSmoke = smokeSelfPlay
}
